import { zodResolver } from '@hookform/resolvers/zod';
import { PhoneNumberTextField } from '@montugroup/design-system';
import type { GetPatientDetailsInclude } from '@montugroup/pms-api-contracts';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import { Grid } from '@mui/material';
import InputAdornment from '@mui/material/InputAdornment';
import type { FieldValues } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { FormContainer, TextFieldElement } from 'react-hook-form-mui';
import type { z } from 'zod';
import FormSection from '../../FormSection';
import { PATIENT_DETAILS_FORM_NAMES } from '../../Patient.constants';
import { schema } from './TreatingDoctor.schema';

export type TreatingDoctorFormFields = z.infer<typeof schema>;

export type TreatingDoctorProps = {
  editable?: boolean;
  defaultValues?: TreatingDoctorFormFields;
  onSubmit?: (data: FieldValues) => void;
  isLoading?: boolean;
};

export const TreatingDoctor = ({ defaultValues, editable, onSubmit, isLoading }: TreatingDoctorProps) => {
  const FORM_NAME: GetPatientDetailsInclude = PATIENT_DETAILS_FORM_NAMES.PATIENT_CARE_INFO;

  const formContext = useForm<TreatingDoctorFormFields>({
    defaultValues,
    values: defaultValues,
    resolver: zodResolver(schema),
  });

  const {
    control,
    formState: { errors },
  } = formContext;

  const isEffectivelyEditable = editable && typeof onSubmit === 'function';

  return (
    <FormSection
      title="Community Doctor"
      formName={FORM_NAME}
      formContext={formContext}
      onSubmit={onSubmit}
      isLoading={isLoading}
    >
      <FormContainer formContext={formContext}>
        <Grid container columnSpacing={10} rowSpacing={5} marginY={2} data-testid="treating-doctor-form">
          <Grid item xs={12} sm={12} lg={6}>
            <TextFieldElement name="gpName" label="Full Name" fullWidth disabled={!editable} />
          </Grid>
          <Grid item xs={12} sm={12} lg={6}>
            <TextFieldElement
              name="gpEmail"
              label="Email"
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <MailOutlineIcon sx={{ color: (theme) => theme.palette.grey[500] }} />
                  </InputAdornment>
                ),
              }}
              disabled={!isEffectivelyEditable}
            />
          </Grid>
          <Grid item xs={12} sm={12} lg={6}>
            <TextFieldElement name="gpClinic" label="Clinic Name" fullWidth disabled={!isEffectivelyEditable} />
          </Grid>
          <Grid item xs={12} sm={12} lg={6}>
            <PhoneNumberTextField
              name="gpPhone"
              label="Phone"
              fullWidth
              control={control}
              error={!!errors?.gpPhone}
              helperText={errors.gpPhone?.message}
              variant={'outlined'}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PhoneIphoneIcon sx={{ color: (theme) => theme.palette.grey[500] }} />
                  </InputAdornment>
                ),
              }}
              disabled={!isEffectivelyEditable}
            />
          </Grid>
        </Grid>
      </FormContainer>
    </FormSection>
  );
};
